<script setup lang="ts">
import t from "~/utils/translate";
import api from "~/utils/auth/api";
import { useQuery } from "@tanstack/vue-query";
import { mdiChevronLeft } from "@mdi/js";
import { IEvent } from "~/types/sports/event";
import Events from "./Events.vue";
import { IOutcome } from "~/types/sports/outcome";
import { IPrice } from "~/types/sports/price";
import { IResult } from "~/types/racing/race-results";
import { IJackpotBet, IJackPotMarket } from "~/types/racing/jackpot-bets";
import NavIcon from "~/components/layout/Nav/NavIcon.vue";
import { DateTime } from "luxon";

const router = useRouter();
const route = useRoute();
const eventsRef = ref<HTMLElement | null>(null);
const isJackPotCompleted = ref<boolean>(false);
const activeSelection = ref<IJackpotBet[]>([]);

const jackpot = computed(() => {
	return { leagueId: route.query.jackpotName, date: route.query.jackpotDate };
});

const jackpotEpochDate = computed(() => {
	const d = jackpot.value.date ? new Date(jackpot.value.date.toString()) : new Date();
	const date = d.getTime() / 1000;
	return date;
});

export interface IJackpotEvent {
	raceNumber: number;
	event: IEvent;
	markets: IJackPotMarket[];
	outcomesAndPrices: {
		outcomes: IOutcome[];
		prices: IPrice[];
	};
}

interface IJackpotDetailsResponse {
	jackpotName: string;
	date: string;
	totalPrizeMoney: number;
	events: IJackpotEvent[];
	raceResults: IResult[];
	isAbandoned: boolean;
	isActive: boolean;
	isCompleted: boolean;
}

const outcomeSelection = (isFavorite: boolean) => {
	if (eventsRef.value && eventsRef.value) {
		if (isFavorite) {
			// @ts-ignore
			eventsRef.value.pickFavorite();
		} else {
			// @ts-ignore
			eventsRef.value.handleOutcomeSelection();
		}
	}
};

const jackpotDetailsApiCall = async () => {
	const response = await api<IJackpotDetailsResponse[]>({
		url: `/Jackpot/JackpotsWithEmop?date=${jackpot.value.date}&countryCode=ZA`,
		type: "horses",
		apiVersion: "v1",
		options: { method: "GET" },
	});
	return response.find((j) => j.jackpotName === jackpot.value.leagueId);
};

const { data: jackpotEmop } = useQuery({
	queryKey: ["Jackpot-details-with-emop", jackpot.value.leagueId, jackpot.value.date],
	queryFn: jackpotDetailsApiCall,
});

const isJackpotOpen = computed(() => {
	const jackpotDate = DateTime.fromISO(jackpot.value.date as string);
	const isToday = jackpotDate.hasSame(DateTime.local(), "day");
	return isToday;
});

const handlePlayerSelection = (selection: IJackpotBet[]) => {
	isJackPotCompleted.value = true;
	activeSelection.value = selection;
};
</script>
<template>
	<div>
		<div class="flex items-center bg-dark-800 p-1 rounded-t-lg text-light-50 leading-tight">
			<div
				@click="router.push({ query: { module: 'jackpot' } })"
				class="flex items-center justify-center flex-shrink-0 w-10 h-8 transition-all cursor-pointer border-r border-r-dark-500"
			>
				<UiIcon :path="mdiChevronLeft" class="w-[25px] h-[25px] fill-light-50" />
			</div>
			<div class="flex items-center justify-between flex-1 ml-2 bg-dark-700 rounded-md p-1">
				<div class="flex items-center leading-tight">
					<NavIcon
						:width="24"
						:height="24"
						:src="`/icons/regions-leagues-flags/south-africa-circular.svg`"
						fallback="/icons/regions-leagues-flags/default-circular.svg"
						class="flex-shrink-0 mr-[2px]"
					/>
					<div>
						<div class="text-xs font-bold capitalize">{{ jackpot.leagueId }}</div>
						<div class="text-xs">
							<UiDateTime class="dark:text-light-50 capitalize text-xs" :date="jackpotEpochDate || ''" dateFormat="EEEE" />
						</div>
					</div>
				</div>

				<div class="text-xs text-right">
					<span class="font-light">
						{{ t("mane-million-jackpot") }}
					</span>
					<div class="font-bold mt-[2px]" v-if="jackpotEmop?.totalPrizeMoney">{{ formatNumber(jackpotEmop?.totalPrizeMoney, "currency", true) }}</div>
				</div>
			</div>
		</div>
		<div class="flex justify-between items-center bg-dark-700 p-1 rounded-b-lg text-light-50 leading-tight text-xs px-2">
			<div v-if="isJackPotCompleted" class="flex items-center">
				{{ t("correct-selection") }} :
				<div class="ml-1 w-8 h-[18px] rounded-md bg-[#E0F3E0] text-dark-800 font-bold flex items-center justify-center">
					{{ activeSelection.filter((s) => s.resultingStatusId === 1).length }}/8
				</div>
			</div>
			<div>
				<span v-if="isJackPotCompleted">{{ t("betslip-id") }} : {{ activeSelection[0].betslipId }}</span>
			</div>
			<div v-if="!isJackPotCompleted">
				<UiButton
					@click="outcomeSelection(false)"
					:size="'small'"
					:variant="'custom'"
					:label="t('quick-pick')"
					class="bg-light-400 rounded-md text-dark-800 text-xs mr-2"
					:disabled="jackpotEmop?.events.some((e) => !e.event.isActive) || isJackPotCompleted || !isJackpotOpen"
				/>
				<UiButton
					@click="outcomeSelection(true)"
					:size="'small'"
					:variant="'custom'"
					:label="t('pick-favourites')"
					class="bg-light-400 rounded-md text-dark-800 text-xs"
					:disabled="jackpotEmop?.events.some((e) => !e.event.isActive) || isJackPotCompleted || !isJackpotOpen"
				/>
			</div>
		</div>

		<div class="my-2 rounded-md p-1 bg-primary-700 text-light-50 text-xs text-center">
			<span v-if="isJackPotCompleted">{{ t("jackpot-selection-has-been-submitted") }}</span>
			<span v-else>
				{{ t("choose-horse-and-confirm-selections") }}
			</span>
		</div>

		<div class="flex justify-center md:justify-end my-2 p-1">
			<div class="flex items-center bg-light-50 dark:bg-dark-700 rounded-lg px-1">
				<div class="flex items-center mx-1 text-xs">
					<div class="w-[16px] h-[16px] rounded-md border-primary-700 bg-primary-600 mr-1" />
					{{ t("your-selection") }}
				</div>
				<div class="flex items-center mx-1 text-xs">
					<div class="w-[16px] h-[16px] rounded-md border-dark-900 bg-dark-800 mx-1" />
					{{ t("picked-to-place") }}
				</div>
				<div class="flex items-center mx-1 text-xs">
					<div class="w-[16px] h-[16px] rounded-md border border-primary-600 bg-override-selection mx-1" />
					{{ t("assigned-horse") }}
				</div>
			</div>
		</div>
		<Events
			ref="eventsRef"
			v-if="jackpotEmop?.events"
			:events="jackpotEmop?.events"
			:raceResults="jackpotEmop.raceResults"
			:jackpotName="jackpot.leagueId?.toString()"
			:isJackpotOpen="isJackpotOpen"
			@jackpot-completed="handlePlayerSelection($event)"
		/>
	</div>
</template>

<style lang="scss" scoped>
.bg-override-selection {
	border: 1px solid var(--primary-l-600-darken-1, #00a826);
	background: linear-gradient(90deg, #9cdb9d 0%, #fff 100%);
}
</style>
